import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, PlayCircle, BookOpen, Bot, X } from 'lucide-react';
import { ref, getDownloadURL } from 'firebase/storage';
import { toast } from 'react-toastify';
import { storage } from '../firebase';
import { PROGRAMS, SUBJECT_CHAPTERS, TUTORIAL_MATERIAL_TABS, TUTORIAL_TEST_ROUTES } from '../config/tutorialCatalogue';
import { useProgress } from '../utils/useProgress';
import { useAuth } from '../context/AuthContext';
import CustomVideoPlayer from '../components/CustomVideoPlayer';
import TutorialAccessGuard from '../components/TutorialAccessGuard';
import Chatbot from '../components/chatbot';
import Footer from './Footer';
import { TutorialNotFound } from './TutorialCourse';
import styles from './TutorialLearning.module.css';

function LectureList({ chapter, selected, onSelect, progress, availability }) {
  return (
    <ol className={styles.lectureList}>
      {(chapter.lectures || []).map((lecture, index) => {
        const status = progress?.lectures?.[lecture.id] || {};
        const upcoming = availability[String(lecture.id)]?.status === 'upcoming';
        return (
          <li key={lecture.id}>
            <button type="button" onClick={() => onSelect(lecture)} disabled={upcoming} aria-current={selected?.id === lecture.id ? 'true' : undefined}>
              <span className={styles.lectureNumber}>{status.done ? <CheckCircle2 size={19} /> : String(index + 1).padStart(2, '0')}</span>
              <span className={styles.lectureCopy}><strong>{lecture.title}</strong><small>{upcoming ? 'Coming soon' : status.done ? 'Completed' : status.pct > 0 ? `${status.pct}% watched` : 'Video lecture'}</small></span>
              {selected?.id === lecture.id && <PlayCircle size={18} />}
            </button>
          </li>
        );
      })}
      {!chapter.lectures?.length && <li className={styles.emptyState}>Lectures for this chapter are coming soon.</li>}
    </ol>
  );
}

function ChapterWorkspace({ program, subject, chapter, chapters }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const lectureId = searchParams.get('lecture');
  const selected = (chapter.lectures || []).find(item => String(item.id) === lectureId) || chapter.lectures?.[0];
  const [availability, setAvailability] = useState({});
  const [retry, setRetry] = useState(0);
  const [playbackError, setPlaybackError] = useState(false);
  const [activeTab, setActiveTab] = useState(TUTORIAL_MATERIAL_TABS[0].id);
  const [fileLoading, setFileLoading] = useState({});
  const [isAiOpen, setIsAiOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const playerRef = useRef(null);
  const assistantRef = useRef(null);
  const mounted = useRef(true);
  const { currentUser } = useAuth();
  const { progress, markLectureProgress, markDppOpened, toggleDppDone, markTestAttempted, getChapterStats } = useProgress(subject.subjectKey);
  const chapterProgress = progress[chapter.id] || {};
  const stats = getChapterStats(chapter.id, chapter);
  const selectedState = selected && availability[String(selected.id)];
  const selectedIndex = chapter.lectures?.findIndex(item => item.id === selected?.id) ?? -1;
  const nextChapter = chapters[chapters.findIndex(item => item.id === chapter.id) + 1];
  const tab = TUTORIAL_MATERIAL_TABS.find(item => item.id === activeTab);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setAvailability({});
    (chapter.lectures || []).forEach(async lecture => {
      try {
        const url = await getDownloadURL(ref(storage, `${chapter.storagePath}/${lecture.fileName}`));
        if (!cancelled) setAvailability(previous => ({ ...previous, [String(lecture.id)]: { status: 'available', url } }));
      } catch (error) {
        const missing = error.code === 'storage/object-not-found';
        if (!missing) console.error('[TutorialChapter] Could not load lecture:', error);
        if (!cancelled) setAvailability(previous => ({ ...previous, [String(lecture.id)]: { status: missing ? 'upcoming' : 'error' } }));
      }
    });
    return () => { cancelled = true; };
  }, [chapter, retry]);

  useEffect(() => { setPlaybackError(false); }, [selected?.id, retry]);

  useEffect(() => {
    const onChange = () => setIsFullScreen(document.fullscreenElement === playerRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  useEffect(() => {
    if (!isAiOpen) return;
    const dialog = assistantRef.current;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = 'hidden';
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
    };
  }, [isAiOpen]);

  const selectLecture = lecture => {
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      next.set('lecture', String(lecture.id));
      return next;
    });
  };

  const toggleFullScreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (playerRef.current?.requestFullscreen) await playerRef.current.requestFullscreen();
      else toast.info('Fullscreen is not supported by this browser.');
    } catch (error) {
      console.error('[TutorialChapter] Fullscreen failed:', error);
      toast.error('Could not open fullscreen. Please try again.');
    }
  };

  const openFile = async resource => {
    const popup = window.open('', '_blank');
    if (!popup) {
      toast.error('Please allow pop-ups to open this practice PDF.');
      return;
    }
    popup.opener = null;
    setFileLoading(previous => ({ ...previous, [resource.fileName]: true }));
    try {
      const url = await getDownloadURL(ref(storage, `${chapter.storagePath}/${resource.fileName}`));
      if (!mounted.current) {
        popup.close();
        return;
      }
      popup.location.href = url;
      markDppOpened(chapter.id, resource.fileName);
    } catch (error) {
      popup.close();
      console.error('[TutorialChapter] Could not open resource:', error);
      toast.error(error.code === 'storage/object-not-found' ? 'This practice PDF is not available yet.' : 'Could not open the PDF. Please check your connection and try again.');
    } finally {
      if (mounted.current) setFileLoading(previous => ({ ...previous, [resource.fileName]: false }));
    }
  };

  const playlist = <LectureList chapter={chapter} selected={selected} onSelect={selectLecture} progress={chapterProgress} availability={availability} />;
  const items = chapter[tab?.source] || [];

  return (
    <div className={styles.page} style={{ '--course-accent': program.gradFrom }}>
      <main className={`${styles.wrapper} ${styles.learningWrapper}`}>
        <nav className={styles.learningBreadcrumb} aria-label="Learning navigation">
          <Link to={`/tutorials/course/${program.id}?subject=${subject.subjectKey}`}><ArrowLeft size={16} /> {program.label} / {subject.title}</Link>
          <span>{chapter.title}</span>
        </nav>
        <header className={styles.chapterHeader}>
          <div><span className={styles.eyebrow}>CHAPTER CLASSROOM</span><h1>{chapter.title}</h1></div>
          <div className={styles.chapterCompletion}><strong>{stats.pct}% complete</strong><span>{stats.done}/{stats.total} activities</span><progress value={stats.pct} max="100" aria-label="Chapter completion" /></div>
        </header>
        <div className={styles.classroom}>
          <section className={styles.watchArea} aria-label="Lecture player">
            <div ref={playerRef} className={styles.playerShell}>
              <div className={styles.videoFrame}>
                {!selected ? <div className={styles.playerMessage}><BookOpen size={40} /><h2>Lectures coming soon</h2><p>You can still explore this chapter's learning materials below.</p></div>
                  : !selectedState ? <div className={styles.playerMessage} role="status"><span className={styles.spinner} /><p>Loading your lecture...</p></div>
                  : selectedState.status === 'upcoming' ? <div className={styles.playerMessage}><PlayCircle size={40} /><h2>This lecture is coming soon</h2><p>Choose another lecture or explore the chapter materials.</p></div>
                  : selectedState.status === 'error' || playbackError ? <div className={styles.playerMessage} role="alert"><h2>Unable to load this lecture</h2><p>Please check your connection and try again.</p><button type="button" onClick={() => setRetry(value => value + 1)}>Try again</button></div>
                  : <CustomVideoPlayer key={`${selected.id}-${retry}`} src={selectedState.url} onAiClick={() => setIsAiOpen(value => !value)} isAiOpen={isAiOpen} onToggleFullScreen={toggleFullScreen} isFullScreen={isFullScreen} onProgressUpdate={pct => markLectureProgress(chapter.id, selected.id, pct)} onError={() => setPlaybackError(true)} />}
              </div>
              <dialog ref={assistantRef} className={styles.assistantDialog} aria-labelledby="learning-assistant-title" onCancel={() => setIsAiOpen(false)} onClick={event => { if (event.target === event.currentTarget) setIsAiOpen(false); }}>
                {isAiOpen && <div className={styles.assistant}>
                  <div className={styles.assistantHeading}><h2 id="learning-assistant-title"><Bot size={20} /> Learning assistant</h2><button type="button" onClick={() => setIsAiOpen(false)} aria-label="Close learning assistant"><X size={20} /></button></div>
                  <Chatbot isEmbedded />
                </div>}
              </dialog>
            </div>
            <div className={styles.nowPlaying}>
              <div><span className={styles.eyebrow}>NOW LEARNING {selectedIndex >= 0 && `/ ${selectedIndex + 1} OF ${chapter.lectures.length}`}</span><h2>{selected?.title || 'Explore your chapter materials'}</h2></div>
              <div className={styles.playerActions}>
                <button type="button" onClick={() => selectLecture(chapter.lectures[selectedIndex - 1])} disabled={selectedIndex <= 0} aria-label="Previous lecture"><ArrowLeft size={17} /></button>
                <button type="button" onClick={() => selectLecture(chapter.lectures[selectedIndex + 1])} disabled={selectedIndex < 0 || selectedIndex >= chapter.lectures.length - 1} aria-label="Next lecture"><ArrowRight size={17} /></button>
                <button type="button" aria-haspopup="dialog" aria-expanded={isAiOpen} onClick={() => setIsAiOpen(true)}><Bot size={17} /> Ask AI</button>
              </div>
            </div>
          </section>
          <aside className={styles.playlist} aria-label="Chapter lecture playlist">
            <div className={styles.playlistHeader}><h2>Chapter playlist</h2><p>{stats.doneLecs}/{stats.totalLecs} lectures completed</p><progress value={stats.doneLecs} max={stats.totalLecs || 1} aria-label="Lecture completion" /></div>
            <div className={styles.playlistScroll}>{playlist}</div>
          </aside>
          <section className={styles.materials} aria-label="Chapter learning materials">
            <div className={styles.materialTabs} role="group" aria-label="Choose learning materials">
              {TUTORIAL_MATERIAL_TABS.map(item => <button type="button" key={item.id} aria-pressed={activeTab === item.id} onClick={() => setActiveTab(item.id)}>{item.label}{item.source && <span>{chapter[item.source]?.length || 0}</span>}</button>)}
            </div>
            <div className={styles.materialPanel}>
              <h2>{tab.label}</h2>
              {tab.kind === 'lectures' && playlist}
              {tab.kind === 'files' && (items.length ? <div className={styles.resourceList}>{items.map(resource => {
                const saved = chapterProgress.dpps?.[resource.fileName];
                return <div className={styles.resourceRow} key={resource.fileName}><div><strong>{resource.name}</strong><small>{saved?.done ? 'Completed' : saved?.opened ? 'Opened - ready to practice' : 'Download and practice at your own pace'}</small></div><div className={styles.resourceActions}><button type="button" disabled={fileLoading[resource.fileName]} onClick={() => openFile(resource)}>{fileLoading[resource.fileName] ? 'Opening...' : 'Open PDF'}</button><button type="button" aria-pressed={!!saved?.done} disabled={!currentUser} onClick={() => toggleDppDone(chapter.id, resource.fileName)}>{saved?.done ? 'Completed' : 'Mark done'}</button></div></div>;
              })}</div> : <p className={styles.emptyState}>Practice materials for this chapter are coming soon.</p>)}
              {tab.kind === 'tests' && (items.length ? <div className={styles.resourceList}>{items.map(test => <div className={styles.resourceRow} key={test.testId}><div><strong>{test.name}</strong><small>{!TUTORIAL_TEST_ROUTES[test.testId] ? 'Coming soon' : chapterProgress.tests?.[test.testId]?.completed ? 'Completed' : chapterProgress.tests?.[test.testId]?.attempted ? 'Started - submit to complete' : 'Not attempted yet'}</small></div>{TUTORIAL_TEST_ROUTES[test.testId] ? <Link className={styles.primaryLink} to={TUTORIAL_TEST_ROUTES[test.testId]} onClick={() => markTestAttempted(chapter.id, test.testId)}>Start test <ArrowRight size={16} /></Link> : <span className={styles.status}>Coming soon</span>}</div>)}</div> : <p className={styles.emptyState}>Mock tests for this chapter are coming soon.</p>)}
              {tab.kind === 'link' && <div className={styles.resourceRow}><p>Explore {tab.label.toLowerCase()} to support your learning.</p><Link className={styles.primaryLink} to={tab.route}>Open {tab.label.toLowerCase()} <ArrowRight size={16} /></Link></div>}
            </div>
          </section>
        </div>
        <div className={styles.chapterFooter}><Link to={`/tutorials/course/${program.id}?subject=${subject.subjectKey}`}>All {subject.title.toLowerCase()} chapters</Link>{nextChapter && <Link to={`/tutorials/course/${program.id}/${subject.subjectKey}/${nextChapter.id}`}>Next chapter <ArrowRight size={16} /></Link>}</div>
      </main>
      <Footer />
    </div>
  );
}

export default function TutorialChapter() {
  const { programId, subjectKey, chapterId } = useParams();
  const program = PROGRAMS.find(item => item.id === programId);
  const subject = program?.subjects.find(item => item.subjectKey === subjectKey);
  const chapters = SUBJECT_CHAPTERS[subjectKey] || [];
  const chapter = chapters.find(item => item.id === chapterId);
  if (!program || !subject || subject.comingSoon || !chapter) return <TutorialNotFound />;
  return (
    <TutorialAccessGuard programIds={[program.id]}>
      <ChapterWorkspace key={`${subjectKey}/${chapterId}`} program={program} subject={subject} chapter={chapter} chapters={chapters} />
    </TutorialAccessGuard>
  );
}
